import { FinancingConfigDto, Merchant } from '@applyonline/shared'
import i18n from '@if/core/i18n'
import { EBeneficiaryType, ELoanPurpose } from '@if/core/types'
import { useCallback, useMemo, useState } from 'react'
import { isDefaultMerchant, isMedicardMerchant } from '../../data/merchant-selectors'
import { getDefaultMerchantIdFromHostname } from '../../services/query-string'
import { Prequalification, buildPrequalificationSchemaObject } from '../../types/Schemas/PrequalificationSchema'

const STEP_LIST_FOR_APPLICANT = ['personal-information', 'contact', 'employment', 'address', 'review']
export function useStepListForApplicant() {
  return STEP_LIST_FOR_APPLICANT
}

export function useNewApplicationStepList(merchantId: string) {
  return useMemo(() => {
    const ret = [...STEP_LIST_FOR_APPLICANT]
    if (isDefaultMerchant(merchantId)) {
      ret.unshift('service-provider')
    }

    ret.unshift('loan-amount')
    return ret
  }, [merchantId])
}

export function getMonthlyIncome(applicant: {
  currentJobs?: { annualSalary?: number | null }[]
  otherIncomes?: { annualAmount?: number | null }[]
}): number {
  const annualIncome =
    (applicant.currentJobs ?? []).reduce((sum, job) => sum + Number(job.annualSalary ?? 0), 0) +
    (applicant.otherIncomes ?? []).reduce((sum, income) => sum + Number(income.annualAmount ?? 0), 0)
  return annualIncome / 12
}

export const buildDefaultPrequalification = (
  merchant: Merchant,
  financingProgramConfig: FinancingConfigDto,
): Prequalification => {
  const defaultPrequal: Prequalification = {
    ...(buildPrequalificationSchemaObject(financingProgramConfig).getDefault() as unknown as Prequalification),
    lastTouchOrigination: null,
    firstTouchOrigination: null,
  }

  if (isDefaultMerchant(merchant.id))
    defaultPrequal.loanPurposeId = isMedicardMerchant(getDefaultMerchantIdFromHostname())
      ? ELoanPurpose.Medical
      : ELoanPurpose.GoodsAndServices
  else defaultPrequal.loanPurposeId = merchant.serviceCategory

  if (defaultPrequal.loanPurposeId === ELoanPurpose.Veterinary) {
    defaultPrequal.beneficiaryTypeId = EBeneficiaryType.Other
  } else {
    defaultPrequal.beneficiaryTypeId = EBeneficiaryType.Applicant
  }
  defaultPrequal.merchantId = merchant.id
  defaultPrequal.isInvisalign = false
  defaultPrequal.applicant.isCoapplicant = false
  defaultPrequal.applicant.languageId = i18n.language === 'fr' ? 0 : 1

  return defaultPrequal
}

export const buildDefaultCoapplicant = (financingProgramConfig: FinancingConfigDto): Prequalification => {
  const defaultPrequal = buildPrequalificationSchemaObject(
    financingProgramConfig,
  ).getDefault() as unknown as Prequalification
  defaultPrequal.applicant.isCoapplicant = true
  defaultPrequal.applicant.languageId = i18n.language === 'fr' ? 0 : 1
  return defaultPrequal
}

export const useSessionStorage = <T,>(keyName: string, initialValue: T) => {
  const savedInfo = sessionStorage.getItem(keyName)
  const initValue = savedInfo ? (JSON.parse(savedInfo) as T) : initialValue

  const [value, setValue] = useState<T>(initValue)

  const saveValue = useCallback(
    (newValue: T) => {
      const json = JSON.stringify(newValue)
      sessionStorage.setItem(keyName, json)
      setValue(newValue)
    },
    [keyName],
  )

  return [value, saveValue] as const
}
